import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { get, post } from "@/lib/api";
import { PageHeader } from "@/components/common/PageHeader";
import { FormDialog } from "@/components/common/FormDialog";
import { EmptyState, Skeleton } from "@/components/common/EmptyState";
import { StatusBadge } from "@/components/common/StatusBadge";
import { SearchInput } from "@/components/common/SearchInput";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, FileSearch } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const ACCOUNT_TYPES = ["Asset", "Liability", "Income", "Expense", "Equity"];

export default function ChartOfAccounts() {
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const { data: accounts, isLoading } = useQuery({ queryKey: ["coa"], queryFn: () => get("/finance/chart-of-accounts") });
  const create = useMutation({
    mutationFn: (body) => post("/finance/chart-of-accounts", body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["coa"] }); toast.success("Account created"); },
  });
  const parents = (accounts || []).filter((a) => !a.parent);
  const childrenOf = (id) => (accounts || []).filter((a) => a.parent === id);

  const matches = (a) => {
    if (search && !`${a.code} ${a.name}`.toLowerCase().includes(search.toLowerCase())) return false;
    if (typeFilter !== "all" && a.type !== typeFilter) return false;
    return true;
  };

  // Hierarchical filtering: if a parent itself matches, keep its full subtree intact.
  // Otherwise, show the parent only as context for whichever children match, so the
  // tree/indentation structure never shows an orphaned child row.
  const rows = useMemo(() => {
    return parents
      .map((p) => {
        const children = childrenOf(p.id);
        const parentMatches = matches(p);
        const matchingChildren = parentMatches ? children : children.filter(matches);
        if (!parentMatches && matchingChildren.length === 0) return null;
        return { parent: p, children: matchingChildren };
      })
      .filter(Boolean);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accounts, search, typeFilter]);

  if (isLoading) return <Skeleton className="h-64" />;

  return (
    <div>
      <PageHeader title="Chart of Accounts" subtitle="Your ledger account structure.">
        <FormDialog title="New Account" testid="new-account-modal"
          trigger={<Button data-testid="new-account-btn"><Plus className="mr-1.5 h-4 w-4" />New Account</Button>}
          fields={[
            { name: "code", label: "Account Code", required: true },
            { name: "name", label: "Account Name", required: true },
            { name: "type", label: "Type", type: "select", required: true, options: ACCOUNT_TYPES },
            { name: "parent", label: "Parent Account", type: "select", options: (accounts || []).map((a) => ({ value: a.id, label: a.name })) },
          ]}
          onSubmit={(v) => create.mutateAsync(v)} />
      </PageHeader>

      <div className="mb-4 flex items-center gap-3">
        <SearchInput testid="coa-search" placeholder="Search code or name" value={search} onChange={setSearch} />
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-44" data-testid="coa-type-filter"><SelectValue placeholder="All types" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All types</SelectItem>
            {ACCOUNT_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {rows.length === 0 ? (
        <EmptyState icon={FileSearch}
          title={(accounts || []).length === 0 ? "No accounts yet" : "No accounts found"}
          subtitle={(accounts || []).length === 0 ? "Create your first ledger account to get started." : "Adjust your search or filter."} />
      ) : (
        <div className="overflow-hidden rounded-xl border border-border bg-card">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
              <th className="px-4 py-3 font-semibold">Code</th><th className="px-4 py-3 font-semibold">Account</th>
              <th className="px-4 py-3 font-semibold">Type</th></tr></thead>
            <tbody>
              {rows.map(({ parent: p, children }) => (
                <>
                  <tr key={p.id} className="border-b border-border font-medium">
                    <td className="px-4 py-2.5 font-mono">{p.code}</td><td className="px-4 py-2.5">{p.name}</td>
                    <td className="px-4 py-2.5"><StatusBadge family="slate" label={p.type} /></td>
                  </tr>
                  {children.map((c) => (
                    <tr key={c.id} className="border-b border-border text-muted-foreground">
                      <td className="px-4 py-2.5 pl-8 font-mono">{c.code}</td><td className="px-4 py-2.5 pl-8">{c.name}</td>
                      <td className="px-4 py-2.5"><StatusBadge family="slate" label={c.type} /></td>
                    </tr>
                  ))}
                </>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
